<template>
  <div class="feed-view-container">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb-container mb-6 d-flex align-center flex-wrap">
      <template v-for="(tag, index) in tagStack" :key="index">
        <span 
          class="tag-link text-h5 font-weight-bold" 
          :class="{ 'text-primary': index === tagStack.length - 1, 'text-grey pointer': index < tagStack.length - 1 }"
          @click="index < tagStack.length - 1 ? navigateToTag(index) : null"
        >
          #{{ tag }}
        </span>
        <v-icon v-if="index < tagStack.length - 1" icon="mdi-chevron-right" size="small" class="mx-2 text-grey-darken-1"></v-icon>
      </template>
      <!-- Custom Query Indicator in Breadcrumb -->
      <template v-if="currentCustomQuery">
        <v-icon icon="mdi-chevron-right" size="small" class="mx-2 text-grey-darken-1"></v-icon>
        <span class="tag-link text-h5 font-weight-bold text-primary mr-4">
          {{ customDiscoverName || `"${currentCustomQuery}"` }}
        </span>
        
        <v-btn
          v-if="!isSavedDiscovery"
          color="primary"
          variant="tonal"
          size="small"
          rounded="pill"
          class="text-none font-weight-black"
          @click="$emit('save-discovery', currentCustomQuery)"
        >
          +Save
        </v-btn>
      </template>
    </div>

    <v-alert v-if="!user" type="info" variant="tonal" class="mb-6 rounded-xl border">
      <template v-slot:prepend>
        <v-icon icon="mdi-information-outline"></v-icon>
      </template>
      Login to participate in the community discussion.
    </v-alert>

    <!-- Pinned announcements (home feed only) -->
    <PinnedInfoBanner v-if="isHomeFeed" />

    <div class="centered-feed-content">
      <CreatePost v-if="user" class="mb-40" :initial-tags="composerInitialTags" @success="handlePostSuccess" @convert-to-note="$emit('convert-to-note', $event)" />

      <!-- Curated Feed Header -->
      <v-card 
        v-if="currentListUri && currentListInfo" 
        variant="flat" 
        class="mt-16 mb-10 rounded-xl border border-white-05 overflow-hidden curation-header"
      >
      <v-card-text class="pa-4 pa-sm-6">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <v-avatar size="48" class="mr-4 border border-white-10">
              <v-img v-if="currentListInfo.avatar" :src="currentListInfo.avatar"></v-img>
              <v-icon v-else icon="mdi-star-face" color="primary"></v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black text-white line-clamp-1">{{ currentListInfo.name }}</div>
              <div class="text-caption text-primary font-weight-bold">CURATED FEED</div>
            </div>
          </div>
          <v-btn
            variant="flat"
            color="primary"
            rounded="pill"
            class="px-5"
            @click="showCuratedMembersList"
          >
            <v-icon icon="mdi-account-group-outline" color="black"></v-icon>
            <v-tooltip activator="parent" location="top">
              <span class="text-white">View Members</span>
            </v-tooltip>
          </v-btn>
        </div>
        <div 
          v-if="currentListInfo.description" 
          class="text-body-2 text-grey-lighten-1 mt-2 line-clamp-3"
          style="line-height: 1.6;"
          v-html="renderCuratedDescription(currentListInfo.description)"
          @click="handleContentClick"
        >
        </div>
      </v-card-text>
    </v-card>

    <div v-show="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 text-grey">{{ currentLoadingMessage.replace(/\.\.\.$/, '') }} for {{ currentFeedName }}...</p>
    </div>

    <div v-show="!loading && posts.length > 0" class="posts-container">
      <div>
        <PostItem
          v-for="item in posts"
          :key="item.post?.uri || item.uri"
          :post="item.post || item"
          :reply="item.reply"
          @view-thread="viewThread"
          @reply="viewThread"
          @delete="handleDelete"
          @tag-click="handleTagClick"
          @view-profile="handleViewProfile"
          @quote="$emit('quote', $event)"
        />
      </div>
    </div>

      <v-alert v-if="!loading && posts.length === 0" type="warning" variant="tonal" class="rounded-xl">
        No posts found for {{ currentFeedName }}.
      </v-alert>

      <!-- Load More Button -->
      <div v-if="cursor != null && cursor !== '' && !loading" class="load-more-container d-flex justify-center py-10">
        <v-btn
          color="primary"
          variant="tonal"
          rounded="pill"
          size="large"
          class="px-10 font-weight-black"
          :loading="loadingMore"
          @click="loadMore"
        >
          <v-icon start icon="mdi-chevron-down"></v-icon>
          Load More
        </v-btn>
      </div>
    </div>

    <!-- List Members Dialog -->
    <v-dialog v-model="showListMembers" max-width="500px">
      <v-card class="rounded-xl border border-white-05 bg-surface">
        <v-card-title class="text-h6 font-weight-black pa-4 d-flex align-center justify-space-between">
          <span>Curated Members</span>
          <v-btn icon="mdi-close" variant="text" size="small" @click="showListMembers = false"></v-btn>
        </v-card-title>
        <v-divider class="border-white-05"></v-divider>
        <v-card-text class="pa-0 overflow-y-auto" style="max-height: 60vh;">
          <div v-if="loadingListMembers" class="pa-10 text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <v-list v-else bg-color="transparent">
            <v-list-item
              v-for="member in listMembers"
              :key="member.did"
              class="px-4 py-2"
              @click="handleViewProfile(member.handle); showListMembers = false"
            >
              <template v-slot:prepend>
                <v-avatar size="40" class="mr-3 border border-white-05">
                  <v-img v-if="member.avatar" :src="member.avatar"></v-img>
                  <v-icon v-else icon="mdi-account"></v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-bold text-white">{{ member.displayName || member.handle }}</v-list-item-title>
              <v-list-item-subtitle class="text-grey">@{{ member.handle }}</v-list-item-subtitle>
              <template v-slot:append v-if="user && member.did !== user.did">
                <v-btn
                  v-if="member.viewer?.following"
                  variant="outlined"
                  size="small"
                  rounded="pill"
                  class="text-none font-weight-black"
                  :loading="followingDids.has(member.did)"
                  @click.stop="handleMemberUnfollow(member)"
                >
                  Following
                </v-btn>
                <v-btn
                  v-else
                  color="primary"
                  variant="flat"
                  size="small"
                  rounded="pill"
                  class="text-none font-weight-black"
                  :loading="followingDids.has(member.did)"
                  @click.stop="handleMemberFollow(member)"
                >
                  Follow
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { 
  currentUser, 
  searchGamedevPosts, 
  searchPosts,
  getListFeed,
  getListMembers,
  followUser,
  unfollowUser,
  customDiscovers,
  lastDeletedPostUri
} from '@/services/bluesky';
import { viewThreadByUri } from '@/utils/navigation';
import { handleRichTextClick } from '@/utils/richtext';
import PostItem from '@/components/PostItem.vue';
import CreatePost from '@/components/CreatePost.vue';
import PinnedInfoBanner from '@/components/PinnedInfoBanner.vue';

const emit = defineEmits(['delete', 'quote', 'save-discovery', 'convert-to-note']);

const route = useRoute();
const router = useRouter();
const user = currentUser;

const posts = ref<any[]>([]);
const loading = ref(true);
const currentLoadingMessage = ref("Loading assets...");
const tagStack = ref<string[]>(['gamedevid']);
const currentCustomQuery = ref<string | null>(null);
const currentListUri = ref<string | null>(null);
const currentListInfo = ref<any>(null);
const showListMembers = ref(false);
const listMembers = ref<any[]>([]);
const loadingListMembers = ref(false);
const cursor = ref<string | undefined>(undefined);
const loadingMore = ref(false);
const followingDids = ref<Set<string>>(new Set());

const loadingMessages = [
  "Generating terrain...", "Spawning NPCs...", "Compiling shaders...",
  "Baking lightmaps...", "Loading assets...", "Reticulating splines...",
  "Summoning bosses...", "Placing health potions...", "Calibrating hitboxes...",
  "Polishing pixels...", "Tuning physics engine...", "Calculating pathfinding...",
  "Equipping legendary loot...", "Rendering next frame...", "Initializing AI behaviors...",
  "Connecting to servers...", "Saving progress...", "Unlocking achievements...",
  "Leveling up characters...", "Designing dungeons...", "Writing dialogue trees...",
  "Balancing stats...", "Fixing collision bugs...", "Taming wild geometry...",
  "Warming up the GPU..."
];

const currentTag = computed(() => tagStack.value[tagStack.value.length - 1]);
const isHomeFeed = computed(() => route.path === '/' || route.path === '/curated');

const KNOWN_TAGS = new Set(['gamedevid','gdiprog','gdiengine','gdiart','gdidesign','gdidevlog','gdirelease','gdicollab','gdihelp','gdijob','gdinote']);
const composerInitialTags = computed(() => {
  const tag = currentTag.value;
  if (!tag || tag === 'gamedevid' || !KNOWN_TAGS.has(tag)) return ['gamedevid'];
  return ['gamedevid', tag];
});

const currentFeedName = computed(() => {
  if (currentListUri.value) return 'Curated Feed';
  if (currentCustomQuery.value) {
    const discover = customDiscovers.value.find((d: any) => d.query === currentCustomQuery.value);
    return discover ? (discover as any).name : 'Search';
  }
  return '#' + (currentTag.value || 'gamedevid');
});

const customDiscoverName = computed(() => {
  const discover = customDiscovers.value.find((d: any) => d.query === currentCustomQuery.value);
  return discover ? (discover as any).name : null;
});

const isSavedDiscovery = computed(() => {
  return customDiscovers.value.some((d: any) => d.query === currentCustomQuery.value);
});

const fetchPosts = async (force = false) => {
  loading.value = true;
  cursor.value = undefined;
  currentLoadingMessage.value = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
  try {
    if (currentListUri.value) {
      const [{ feed: feedItems, cursor: nextCursor }, infoRes] = await Promise.all([
        getListFeed(currentListUri.value),
        getListMembers(currentListUri.value, 1)
      ]);
      posts.value = feedItems.filter((item: any) => !item.reply);
      cursor.value = nextCursor;
      currentListInfo.value = infoRes.list;
    } else if (currentCustomQuery.value) {
      const { posts: searchResults, cursor: nextCursor } = await searchPosts(currentCustomQuery.value);
      posts.value = searchResults;
      cursor.value = nextCursor;
    } else {
      const { posts: gamedevResults, cursor: nextCursor } = await searchGamedevPosts(currentTag.value);
      posts.value = gamedevResults;
      cursor.value = nextCursor;
    }
  } catch (err) {
    console.error('Failed to fetch posts:', err);
  } finally {
    loading.value = false;
  }
};

const loadMore = async () => {
  if (loadingMore.value || !cursor.value) return;
  loadingMore.value = true;
  try {
    let newPosts = [];
    let nextCursor;
    
    if (currentListUri.value) {
      const res = await getListFeed(currentListUri.value, 50, cursor.value);
      newPosts = res.feed.filter((item: any) => !item.reply);
      nextCursor = res.cursor;
    } else if (currentCustomQuery.value) {
      const res = await searchPosts(currentCustomQuery.value, 50, cursor.value);
      newPosts = res.posts;
      nextCursor = res.cursor;
    } else {
      const res = await searchGamedevPosts(currentTag.value, cursor.value);
      newPosts = res.posts;
      nextCursor = res.cursor;
    }
    
    posts.value = [...posts.value, ...newPosts];
    cursor.value = nextCursor;
  } catch (err) {
    console.error('Failed to load more posts:', err);
  } finally {
    loadingMore.value = false;
  }
};

const handleTagClick = (tag: string) => {
  router.push(`/t/${tag}`);
};

const handleViewProfile = (handle?: string) => {
  if (handle) router.push(`/profile/${handle}`);
  else if (user.value) router.push(`/profile/${user.value.handle}`);
};

const viewThread = (post: any) => {
  const rkey = post.uri.split('/').pop();
  router.push(`/profile/${post.author.handle}/post/${rkey}`);
};

const handleDelete = (post: any) => {
  emit('delete', post);
};

watch(lastDeletedPostUri, (newUri) => {
  if (newUri) {
    posts.value = posts.value.filter(p => (p.post?.uri || p.uri) !== newUri);
  }
});

const navigateToTag = (index: number) => {
  const targetTag = tagStack.value[index];
  router.push(targetTag === 'gamedevid' ? '/' : `/t/${targetTag}`);
};

const handlePostSuccess = (newPostUri?: string) => {
  if (newPostUri) {
    viewThreadByUri(newPostUri, router, user.value?.handle);
  } else {
    fetchPosts(true);
  }
};

const handleContentClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router, 
    onTagClick: (tag: string) => handleTagClick(tag) 
  });
};

const showCuratedMembersList = async () => {
  if (!currentListUri.value) return;
  loadingListMembers.value = true;
  showListMembers.value = true;
  try {
    const res = await getListMembers(currentListUri.value, 100);
    listMembers.value = res.items.map((i: any) => i.subject);
  } catch (err) {
    console.error('Failed to fetch list members:', err);
  } finally {
    loadingListMembers.value = false;
  }
};

const renderCuratedDescription = (text: string) => {
  if (!text) return '';
  let html = text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" class="text-blue-400">$1</a>');
  html = html.replace(/@([\w.-]+)/g, '<span class="mention text-primary cursor-pointer" data-handle="$1">@$1</span>');
  return html;
};

const handleMemberFollow = async (member: any) => {
  followingDids.value.add(member.did);
  try {
    const res = await followUser(member.did);
    member.viewer = member.viewer || {};
    member.viewer.following = res.uri;
  } catch (e) {
    console.error('Follow failed:', e);
  } finally {
    followingDids.value.delete(member.did);
  }
};

const handleMemberUnfollow = async (member: any) => {
  if (!member.viewer?.following) return;
  followingDids.value.add(member.did);
  try {
    await unfollowUser(member.viewer.following);
    member.viewer.following = undefined;
  } catch (e) {
    console.error('Unfollow failed:', e);
  } finally {
    followingDids.value.delete(member.did);
  }
};

const syncStateWithRoute = () => {
  const path = route.path;
  const searchQ = route.query.q as string;

  if (path === '/') {
    tagStack.value = ['gamedevid'];
    currentListUri.value = null;
    currentCustomQuery.value = null;
  } else if (path === '/curated') {
    tagStack.value = ['gamedevid'];
    currentListUri.value = import.meta.env.VITE_CURATED_LIST_URI || 'at://did:plc:2cccnvyw6kniwujsofvc2nlt/app.bsky.graph.list/3mgsw5xljie2y';
    currentCustomQuery.value = null;
  } else if (path.startsWith('/t/')) {
    const tag = path.split('/')[2] || 'gamedevid';
    currentListUri.value = null;
    tagStack.value = tag === 'gamedevid' ? ['gamedevid'] : ['gamedevid', tag];
    currentCustomQuery.value = null;
  } else if (path.startsWith('/search')) {
    currentCustomQuery.value = searchQ || null;
    currentListUri.value = null;
    tagStack.value = ['gamedevid'];
  }
  
  fetchPosts();
};

onMounted(() => {
  syncStateWithRoute();
});

watch(() => route.fullPath, () => {
  syncStateWithRoute();
});
</script>

<style scoped>
.breadcrumb-container {
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
}
.breadcrumb-container::-webkit-scrollbar {
  display: none;
}
.tag-link {
  transition: color 0.2s;
}
.tag-link.pointer {
  cursor: pointer;
}
.tag-link.pointer:hover {
  color: var(--v-theme-primary) !important;
  opacity: 0.8;
}
.curation-header {
  background: rgba(255, 255, 255, 0.03) !important;
  margin-top: 32px !important;
}
.posts-container {
  display: flex;
  justify-content: center;
  width: 100%;
}
.posts-container > div {
  width: 100%;
  max-width: 800px;
  padding-top: 24px;
}
.centered-feed-content {
  max-width: 800px;
  margin: 0 auto;
}
</style>
